'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { PRESET_EMOJIS, QUICK_EMOJI } from './emojiPresets';
import { emojiReactionHint, pickEmojiReaction, tapEmojiReaction } from './emojiReactionLogic';
import './emoji-reaction.css';

const PICKER_CLOSE_MS = 150;

type Props = {
  disabled?: boolean;
  selectedEmoji?: string | null;
  onReact: (emoji: string | null) => void | Promise<void>;
  ariaLabel?: string;
};

export function EmojiReactionButton({
  disabled,
  selectedEmoji,
  onReact,
  ariaLabel = 'React with emoji',
}: Props) {
  const pickerId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function openPicker() {
    if (disabled) return;
    clearCloseTimer();
    setOpen(true);
  }

  function scheduleClosePicker() {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpen(false), PICKER_CLOSE_MS);
  }

  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  function handleTap() {
    if (disabled) return;
    void onReact(tapEmojiReaction(selectedEmoji ?? null, QUICK_EMOJI));
  }

  async function pickEmoji(emoji: string) {
    setOpen(false);
    await onReact(pickEmojiReaction(selectedEmoji ?? null, emoji));
  }

  const active = Boolean(selectedEmoji);
  const hint = emojiReactionHint(selectedEmoji ?? null);

  return (
    <div
      className="emoji-reaction"
      ref={rootRef}
      onMouseEnter={openPicker}
      onMouseLeave={scheduleClosePicker}
      onFocusCapture={openPicker}
      onBlurCapture={(e) => {
        if (!rootRef.current?.contains(e.relatedTarget as Node)) {
          scheduleClosePicker();
        }
      }}
    >
      <button
        type="button"
        disabled={disabled}
        className={`post-actions__icon-btn emoji-reaction__trigger${active ? ' post-actions__icon-btn--active emoji-reaction__trigger--selected' : ''}`}
        aria-label={selectedEmoji ? `${ariaLabel}: ${selectedEmoji}` : ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? pickerId : undefined}
        title={hint}
        onClick={() => handleTap()}
      >
        {selectedEmoji ? (
          <span className="emoji-reaction__glyph" aria-hidden>
            {selectedEmoji}
          </span>
        ) : (
          <i className="uil uil-thumbs-up" aria-hidden />
        )}
      </button>
      {open && (
        <div id={pickerId} className="emoji-reaction__picker" role="menu" aria-label="Choose reaction">
          {PRESET_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              className={`emoji-reaction__picker-item${selectedEmoji === emoji ? ' emoji-reaction__picker-item--selected' : ''}`}
              title={selectedEmoji === emoji ? `Remove ${emoji}` : `React ${emoji}`}
              aria-label={selectedEmoji === emoji ? `Remove ${emoji}` : `React ${emoji}`}
              onClick={() => void pickEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
